import { useToggle } from "@mantine/hooks";
import { useForm, joiResolver } from "@mantine/form";
import {
  TextInput,
  PasswordInput,
  Text,
  Paper,
  Group,
  PaperProps,
  Button,
  Divider,
  Anchor,
  Container,
} from "@mantine/core";
import "./register-form.styles.css";
import { useNavigate } from "react-router-dom";
import { FacebookButton } from "../social-buttons/facebook-button.component";
import PersonalInfoForm from "./personal-info-form.component";
import useAuth from "../../hooks/useAuth";
import { useEffect, useState } from "react";
import { SignUpSchema } from "../../schemas/signup.schema";
import PhoneCountryCodeIcon from "../icons/phone-country-code-icon.component";

export interface RegisterFormValue {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  pin: string;
}
function RegisterForm(props: PaperProps<"div">) {
  const navigate = useNavigate();
  const { auth, setAuth } = useAuth();
  const [type, toggle] = useToggle("userInfo", ["userInfo", "personalInfo"]);
  const [loading, setLoading] = useState<boolean>(false);

  const form = useForm<RegisterFormValue>({
    initialValues: {
      email: auth?.continueRegistration?.email
        ? auth.continueRegistration.email
        : "",
      username: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      firstName: auth?.continueRegistration?.firstName
        ? auth.continueRegistration.firstName
        : "",
      middleName: "",
      lastName: auth?.continueRegistration?.lastName
        ? auth.continueRegistration.lastName
        : "",
      pin: "",
    },
    schema: joiResolver(SignUpSchema),
  });

  const handleSubmit = () => {
    toggle();
  };

  const navigateLogin = () => {
    navigate("/login");
  };

  useEffect(() => {
    if (auth?.continueRegistration) {
      form.setFieldValue("email", auth.continueRegistration.email);

      form.setFieldValue("firstName", auth.continueRegistration.firstName);
      form.setFieldValue("lastName", auth.continueRegistration.lastName);
    }

    return () => {
      if (auth?.continueRegistration) {
        // Remove pre-filled inputs when leaving register form
        setAuth({ ...auth, continueRegistration: undefined });
      }
    };
  }, [loading, auth]);

  return (
    <Container size="xl" className="register-form">
      <Paper radius="md" p="xl" withBorder shadow="lg" {...props}>
        {type === "userInfo" && (
          <>
            <Text size="lg" weight={500} align="center">
              Create an account
            </Text>

            <Group mb="md" mt="md" className="group" grow>
              <FacebookButton radius="sm" />
            </Group>

            <Divider
              label="or continue with email"
              labelPosition="center"
              my="lg"
            />

            <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
              <Group direction="column" grow>
                <TextInput
                  required
                  label="Username"
                  autoComplete="username"
                  placeholder="Username"
                  value={form.values.username}
                  onChange={(event) =>
                    form.setFieldValue("username", event.currentTarget.value)
                  }
                  error={form.errors.username}
                  maxLength={50}
                  data-cy="username"
                />

                <TextInput
                  required
                  label="Email"
                  placeholder="Email address"
                  value={form.values.email}
                  onChange={(event) =>
                    form.setFieldValue("email", event.currentTarget.value)
                  }
                  error={form.errors.email}
                  disabled={auth?.continueRegistration?.email ? true : false}
                  maxLength={100}
                  data-cy="email"
                />

                <TextInput
                  required
                  label="Mobile number"
                  // placeholder='926XXXXXXX'
                  value={form.values.phoneNumber}
                  onChange={(event) =>
                    form.setFieldValue("phoneNumber", event.currentTarget.value)
                  }
                  error={form.errors.phoneNumber}
                  icon={<PhoneCountryCodeIcon />}
                  iconWidth={48}
                  maxLength={30}
                  data-cy="phoneNumber"
                />

                <PasswordInput
                  required
                  id="password"
                  name="password"
                  label="Password"
                  autoComplete="password"
                  placeholder="Password"
                  value={form.values.password}
                  onChange={(event) =>
                    form.setFieldValue("password", event.currentTarget.value)
                  }
                  error={form.errors.password}
                  maxLength={22}
                  description="Password must be at least 8 characters and includes at least one upper and lower case letter, number/s, and special character."
                  data-cy="password"
                />

                <PasswordInput
                  required
                  id="confirm_password"
                  label="Confirm password"
                  placeholder="Confirm password"
                  value={form.values.confirmPassword}
                  onChange={(event) =>
                    form.setFieldValue(
                      "confirmPassword",
                      event.currentTarget.value
                    )
                  }
                  error={form.errors.confirmPassword}
                  maxLength={22}
                  data-cy="confirmPassword"
                />
              </Group>

              <Group position="apart" mt="xl">
                <Anchor
                  component="button"
                  type="button"
                  color="gray"
                  onClick={navigateLogin}
                  size="xs"
                >
                  Already have an account? Login
                </Anchor>
                <Button type="submit" data-cy="continueButton">
                  Continue
                </Button>
              </Group>
            </form>
          </>
        )}

        {type === "personalInfo" && (
          <PersonalInfoForm
            toggle={toggle}
            firstForm={form}
            loading={loading}
            setLoading={setLoading}
          />
        )}
      </Paper>
    </Container>
  );
}

export default RegisterForm;
